import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../utils/translations';
import { PROPERTY_TYPE_OPTIONS } from '../../constants/propertyTypes';
import { LISTING_STATUS, resolveListingStatus } from '../../constants/listingStatus';
import { getAlbanianCityOptions, isKnownAlbanianCity, parsePropertyCity } from '../../constants/albanianCities';
import './PropertyFormModal.css';

const PropertyFormModal = ({ isOpen, onClose, property, onSave, defaultAgentId = '' }) => {
  const { t } = useTranslation();
  const isEditMode = !!property;

  const [agents, setAgents] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    location: 'Tirana',
    address: '',
    price: '',
    currency: 'EUR',
    area: '',
    bedrooms: '',
    bathrooms: '',
    floor: '',
    furnished: false,
    elevator: false,
    hipoteke: false,
    exclusive: false,
    propertyType: 'apartment',
    transactionType: 'sale',
    description: '',
    listingStatus: LISTING_STATUS.NONE,
    images: [''],
    video: '',
    agentId: '',
  });
  const [videoUploading, setVideoUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchAgents = async () => {
      try {
        const { agentsAPI } = await import('../../services/api');
        const data = await agentsAPI.getAll();
        setAgents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching agents:', error);
        setAgents([]);
      }
    };

    fetchAgents();
  }, [isOpen]);

  useEffect(() => {
    if (property) {
      setFormData({
        title: property.title || '',
        location: parsePropertyCity(property.location),
        address: property.address || '',
        price: property.price || '',
        currency: property.currency || 'EUR',
        area: property.area || '',
        bedrooms: property.bedrooms || '',
        bathrooms: property.bathrooms || '',
        floor: property.floor || '',
        furnished: property.furnished || false,
        elevator: property.elevator || false,
        hipoteke: property.hipoteke || false,
        exclusive: property.exclusive || false,
        propertyType: property.propertyType || 'apartment',
        transactionType: property.transactionType || 'sale',
        description: property.description || '',
        listingStatus: resolveListingStatus(property),
        images: property.images && property.images.length > 0 ? property.images : [''],
        video: property.video || '',
        agentId: property.agentId || '',
      });
    } else {
      // Reset form for new property
      setFormData({
        title: '',
        location: 'Tirana',
        address: '',
        price: '',
        currency: 'EUR',
        area: '',
        bedrooms: '',
        bathrooms: '',
        floor: '',
        furnished: false,
        elevator: false,
        hipoteke: false,
        exclusive: false,
        propertyType: 'apartment',
        transactionType: 'sale',
        description: '',
        listingStatus: LISTING_STATUS.NONE,
        images: [''],
        video: '',
        agentId: defaultAgentId || '',
      });
    }
  }, [property, isOpen, defaultAgentId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'currency' && value === 'ON_REQUEST') {
      setFormData({
        ...formData,
        currency: value,
        price: '',
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const isPriceOnRequest = formData.currency === 'ON_REQUEST';

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({ ...formData, images: newImages });
  };

  const handleImageUpload = async (index, file) => {
    if (file && file.type.startsWith('image/')) {
      try {
        const { uploadAPI } = await import('../../services/api');
        const imageUrl = await uploadAPI.uploadImage(file);
        const newImages = [...formData.images];
        newImages[index] = imageUrl;
        setFormData({ ...formData, images: newImages });
      } catch (error) {
        console.error('Error uploading image:', error);
        // Fallback to base64 if upload fails
        const reader = new FileReader();
        reader.onloadend = () => {
          const newImages = [...formData.images];
          newImages[index] = reader.result;
          setFormData({ ...formData, images: newImages });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;

    const isVideo =
      file.type.startsWith('video/') ||
      /\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(file.name || '');

    if (!isVideo) {
      alert(t('admin.form.videoUploadError') || 'Failed to upload video');
      return;
    }

    try {
      setVideoUploading(true);
      const { uploadAPI } = await import('../../services/api');
      const videoUrl = await uploadAPI.uploadVideo(file);
      setFormData({ ...formData, video: videoUrl });
    } catch (error) {
      console.error('Error uploading video:', error);
      alert(error.message || t('admin.form.videoUploadError') || 'Failed to upload video');
    } finally {
      setVideoUploading(false);
    }
  };

  const removeVideo = () => {
    setFormData({ ...formData, video: '' });
  };

  const addImageField = () => {
    setFormData({
      ...formData,
      images: [...formData.images, ''],
    });
  };

  const removeImageField = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages.length > 0 ? newImages : [''] });
  };

  const isBase64Image = (str) => {
    return str && str.startsWith('data:image/');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const propertyData = {
      ...formData,
      id: property?.id || null,
      price: isPriceOnRequest ? null : parseFloat(formData.price),
      area: parseFloat(formData.area), // Already using parseFloat for decimals
      bedrooms: parseInt(formData.bedrooms),
      bathrooms: parseInt(formData.bathrooms),
      floor: formData.floor ? parseInt(formData.floor) : null,
      furnished: formData.furnished,
      elevator: formData.elevator,
      hipoteke: formData.hipoteke,
      exclusive: formData.exclusive,
      listingStatus: formData.listingStatus || LISTING_STATUS.NONE,
      images: formData.images.filter(img => img.trim() !== ''),
      video: formData.video || null,
      agentId: formData.agentId || null,
    };
    onSave(propertyData);
  };

  const cityOptions = getAlbanianCityOptions();
  const unknownCity = formData.location && !isKnownAlbanianCity(formData.location)
    ? formData.location
    : '';

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? t('admin.form.editProperty') : t('admin.form.addProperty')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="property-form">
          <div className="form-group">
            <label>{t('admin.form.agent')} *</label>
            <select
              name="agentId"
              value={formData.agentId}
              onChange={handleChange}
              required
            >
              <option value="">{t('admin.form.selectAgent')}</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.form.title')} *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder={t('admin.form.titlePlaceholder') || 'e.g., Luxury Apartment in City Center'}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('admin.form.location')} *</label>
              <select
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
              >
                {!formData.location && (
                  <option value="">{t('admin.form.selectCity') || 'Select city'}</option>
                )}
                {unknownCity && (
                  <option value={unknownCity}>{unknownCity}</option>
                )}
                {cityOptions.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>{t('admin.form.address')}</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.form.currency')}</label>
              <select name="currency" value={formData.currency} onChange={handleChange}>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="LEK">LEK</option>
                <option value="ON_REQUEST">{t('admin.form.priceOnRequest')}</option>
              </select>
            </div>
            {!isPriceOnRequest && (
              <div className="form-group">
                <label>{t('admin.form.price')} *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.form.propertyType')} *</label>
              <select name="propertyType" value={formData.propertyType} onChange={handleChange} required>
                {PROPERTY_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>
                    {t(`propertyTypes.${type}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('admin.form.transactionType')} *</label>
              <select name="transactionType" value={formData.transactionType} onChange={handleChange} required>
                <option value="sale">{t('transactionTypes.sale')}</option>
                <option value="rent">{t('transactionTypes.rent')}</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.form.area')} (m²) *</label>
              <input
                type="number"
                name="area"
                value={formData.area}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>{t('admin.form.bedrooms')} *</label>
              <input
                type="number"
                name="bedrooms"
                value={formData.bedrooms}
                onChange={handleChange}
                required
                min="0"
              />
            </div>
            <div className="form-group">
              <label>{t('admin.form.bathrooms')}</label>
              <input
                type="number"
                name="bathrooms"
                value={formData.bathrooms}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="form-group">
              <label>{t('admin.form.floor')}</label>
              <input
                type="number"
                name="floor"
                value={formData.floor}
                onChange={handleChange}
                min="0"
                placeholder={t('admin.form.floorPlaceholder') || 'e.g., 1, 2, 3...'}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="furnished"
                  checked={formData.furnished}
                  onChange={handleChange}
                />
                {' '}{t('admin.form.furnished')}
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="elevator"
                  checked={formData.elevator}
                  onChange={handleChange}
                />
                {' '}{t('admin.form.elevator')}
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="hipoteke"
                  checked={formData.hipoteke}
                  onChange={handleChange}
                />
                {' '}{t('admin.form.hipoteke')}
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="exclusive"
                  checked={formData.exclusive}
                  onChange={handleChange}
                />
                {' '}{t('admin.form.exclusive')}
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>{t('admin.form.description')}</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
            />
          </div>

          <div className="form-group">
            <label>{t('admin.form.images')}</label>
            {formData.images.map((image, index) => (
              <div key={index} className="image-upload-group">
                {image && (isBase64Image(image) || image.startsWith('http')) && (
                  <div className="image-preview">
                    <img src={image} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => handleImageChange(index, '')}
                      className="clear-image-btn"
                      title={t('admin.form.removeImage')}
                    >
                      ×
                    </button>
                  </div>
                )}
                <div className="image-input-row">
                  <input
                    type="url"
                    value={isBase64Image(image) ? '' : image}
                    onChange={(e) => handleImageChange(index, e.target.value)}
                    placeholder={t('admin.form.imageUrl')}
                    className="image-url-input"
                  />
                  <label className="file-upload-label">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleImageUpload(index, e.target.files[0]);
                        }
                      }}
                      className="file-input"
                    />
                    <span className="file-upload-button">{t('admin.form.uploadImage')}</span>
                  </label>
                  {formData.images.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeImageField(index)}
                      className="remove-image-btn"
                      title={t('admin.form.removeField')}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addImageField}
              className="add-image-btn"
            >
              + {t('admin.form.addImage')}
            </button>
          </div>

          <div className="form-group">
            <label>{t('admin.form.video')}</label>
            {formData.video ? (
              <div className="video-upload-group">
                <video src={formData.video} controls className="video-preview" />
                <button
                  type="button"
                  onClick={removeVideo}
                  className="remove-video-btn"
                >
                  {t('admin.form.removeVideo')}
                </button>
              </div>
            ) : (
              <label className="file-upload-label">
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/x-m4v,.mp4,.webm,.mov,.m4v"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleVideoUpload(e.target.files[0]);
                      e.target.value = '';
                    }
                  }}
                  className="file-input"
                  disabled={videoUploading}
                />
                <span className="file-upload-button">
                  {videoUploading ? t('admin.form.uploadingVideo') : t('admin.form.uploadVideo')}
                </span>
              </label>
            )}
            <p className="field-hint">{t('admin.form.videoHint')}</p>
          </div>

          <div className="form-group listing-status-group">
            <label className="listing-status-label">{t('admin.form.listingStatus')}</label>
            <div className="listing-status-options">
              <label className="listing-status-option">
                <input
                  type="radio"
                  name="listingStatus"
                  value={LISTING_STATUS.NONE}
                  checked={formData.listingStatus === LISTING_STATUS.NONE}
                  onChange={handleChange}
                />
                <span>{t('admin.form.listingStatusNone')}</span>
              </label>
              <label className="listing-status-option">
                <input
                  type="radio"
                  name="listingStatus"
                  value={LISTING_STATUS.RESERVED}
                  checked={formData.listingStatus === LISTING_STATUS.RESERVED}
                  onChange={handleChange}
                />
                <span>{t('propertyDetails.reserved')}</span>
              </label>
              <label className="listing-status-option">
                <input
                  type="radio"
                  name="listingStatus"
                  value={LISTING_STATUS.SOLD}
                  checked={formData.listingStatus === LISTING_STATUS.SOLD}
                  onChange={handleChange}
                />
                <span>{t('propertyDetails.sold')}</span>
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              {t('admin.form.cancel')}
            </button>
            <button type="submit" className="btn-save">
              {isEditMode ? t('admin.form.update') : t('admin.form.add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyFormModal;
